import { useState } from "react";
import { Table, Button, Modal, Form, Select, DatePicker, InputNumber, Popconfirm, message, Space, Tag, Switch } from "antd";
import { Plus } from "lucide-react";
import dayjs from "dayjs";
import { useHRUsers, useSickness, useSicknessCreate, useSicknessUpdate, useSicknessDelete } from "../../../../api-hooks/hr";

const CATEGORIES = ["cold_flu","musculoskeletal","mental_health","injury","other"];

export default function SicknessTab({ cid }: { cid: number }) {
  const [msgApi, ctx] = message.useMessage();
  const [form] = Form.useForm();
  const [modal, setModal] = useState<{ open: boolean; rec: any }>({ open: false, rec: null });
  const [page, setPage] = useState(1);

  const { data: users = [] } = useHRUsers(cid);
  const { data: sick, isLoading } = useSickness(cid, { page, limit: 50 });
  const rows = (sick as any)?.data ?? [];
  const total = (sick as any)?.total ?? 0;

  const cb = (msg: string, fn?: () => void) => ({ onSuccess: () => { msgApi.success(msg); fn?.(); }, onError: (e: any) => msgApi.error(e?.message || "Error") });
  const create = useSicknessCreate(cid, cb("Created.", () => setModal({ open: false, rec: null })));
  const upd = useSicknessUpdate(cid, cb("Updated.", () => setModal({ open: false, rec: null })));
  const del = useSicknessDelete(cid, { onSuccess: () => msgApi.success("Deleted."), onError: (e: any) => msgApi.error(e?.message) });

  const cols = [
    { title: "Employee", dataIndex: "user_name", key: "u" },
    { title: "Start", dataIndex: "start_date", key: "s", render: (v: string) => v ? dayjs(v).format("DD/MM/YY") : "—" },
    { title: "Return", dataIndex: "return_date", key: "r", render: (v: string) => v ? dayjs(v).format("DD/MM/YY") : <Tag color="orange">Ongoing</Tag> },
    { title: "Days", dataIndex: "days_absent", key: "d" },
    { title: "Category", dataIndex: "reason_category", key: "rc", render: (v: string) => v?.replace(/_/g," ").replace(/\b\w/g, c => c.toUpperCase()) },
    { title: "SSP", dataIndex: "ssp_eligible", key: "ssp", render: (v: number) => <Tag color={v ? "green" : "default"}>{v ? "Yes" : "No"}</Tag> },
    { title: "RTW Done", dataIndex: "return_to_work_completed", key: "rtw", render: (v: number) => <Tag color={v ? "green" : "orange"}>{v ? "Yes" : "No"}</Tag> },
    { title: "", key: "act", render: (_: any, r: any) => (
      <Space size="small">
        <Button size="small" onClick={() => { setModal({ open: true, rec: r }); form.setFieldsValue({ ...r, start_date: r.start_date ? dayjs(r.start_date) : null, return_date: r.return_date ? dayjs(r.return_date) : null, ssp_start_date: r.ssp_start_date ? dayjs(r.ssp_start_date) : null, ssp_end_date: r.ssp_end_date ? dayjs(r.ssp_end_date) : null }); }}>Edit</Button>
        <Popconfirm title="Delete?" onConfirm={() => del.mutate({ id: r.id })} okButtonProps={{ danger: true }}><Button size="small" danger>Del</Button></Popconfirm>
      </Space>
    )},
  ];

  return (
    <div>
      {ctx}
      <div className="flex justify-end mb-3">
        <Button size="small" type="primary" icon={<Plus size={13} />} onClick={() => { setModal({ open: true, rec: null }); form.resetFields(); form.setFieldsValue({ reason_category: "other", self_certified: true, ssp_eligible: true, return_to_work_completed: false }); }} style={{ background: "#4c1d95" }}>Add Absence</Button>
      </div>
      <Table rowKey="id" dataSource={rows} columns={cols} loading={isLoading} size="small" pagination={{ current: page, pageSize: 50, total, onChange: setPage }} />
      <Modal open={modal.open} onCancel={() => setModal({ open: false, rec: null })} footer={null} destroyOnClose title={modal.rec ? "Edit Absence" : "New Sickness Absence"} width={520}>
        <Form form={form} layout="vertical" size="middle" className="pt-2" onFinish={(vals) => {
          const p = { ...vals, start_date: vals.start_date?.format("YYYY-MM-DD"), return_date: vals.return_date?.format("YYYY-MM-DD"), ssp_start_date: vals.ssp_start_date?.format("YYYY-MM-DD"), ssp_end_date: vals.ssp_end_date?.format("YYYY-MM-DD") };
          if (modal.rec) upd.mutate({ id: modal.rec.id, ...p }); else create.mutate(p);
        }}>
          <Form.Item name="user_id" label="Employee" rules={[{ required: true }]}><Select options={(users as any[])?.map(u => ({ value: u.id, label: u.name }))} showSearch optionFilterProp="label" /></Form.Item>
          <Form.Item name="reason_category" label="Category" rules={[{ required: true }]}><Select options={CATEGORIES?.map(v => ({ value: v, label: v.replace(/_/g," ").replace(/\b\w/g, c => c.toUpperCase()) }))} /></Form.Item>
          <div className="grid grid-cols-3 gap-x-3">
            <Form.Item name="start_date" label="Start Date" rules={[{ required: true }]}><DatePicker className="w-full" format="DD/MM/YYYY" /></Form.Item>
            <Form.Item name="return_date" label="Return Date"><DatePicker className="w-full" format="DD/MM/YYYY" /></Form.Item>
            <Form.Item name="days_absent" label="Days Absent"><InputNumber className="w-full" min={0} step={0.5} /></Form.Item>
          </div>
          <div className="grid grid-cols-3 gap-x-3">
            <Form.Item name="self_certified" label="Self Certified" valuePropName="checked"><Switch /></Form.Item>
            <Form.Item name="ssp_eligible" label="SSP Eligible" valuePropName="checked"><Switch /></Form.Item>
            <Form.Item name="return_to_work_completed" label="RTW Done" valuePropName="checked"><Switch /></Form.Item>
          </div>
          <div className="grid grid-cols-2 gap-x-3">
            <Form.Item name="ssp_start_date" label="SSP Start"><DatePicker className="w-full" format="DD/MM/YYYY" /></Form.Item>
            <Form.Item name="ssp_end_date" label="SSP End"><DatePicker className="w-full" format="DD/MM/YYYY" /></Form.Item>
          </div>
          <div className="flex gap-2 pt-1">
            <Button type="primary" htmlType="submit" style={{ background: "#4c1d95" }} loading={create.isPending || upd.isPending}>Save</Button>
            <Button onClick={() => setModal({ open: false, rec: null })}>Cancel</Button>
          </div>
        </Form>
      </Modal>
    </div>
  );
}
